package link.erpango.quip.core

import androidx.lifecycle.ViewModelStore
import androidx.lifecycle.ViewModelStoreOwner
import link.erpango.quip.core.factory.AuthFactory
import link.erpango.quip.core.factory.GarageFactory
import link.erpango.quip.core.factory.MySparePartsFactory
import link.erpango.quip.core.factory.NoteImportFactory
import link.erpango.quip.core.factory.SettingsFactory
import link.erpango.quip.core.interactor.AuthInteractor
import link.erpango.quip.core.interactor.GarageInteractor
import link.erpango.quip.core.interactor.MySparePartsInteractor
import link.erpango.quip.core.interactor.NoteImportBulkCreator
import link.erpango.quip.core.interactor.NoteImportInteractor
import link.erpango.quip.data.IDataSource
import link.erpango.quip.utils.IFileUtils
import link.erpango.quip.utils.IPlatformManager
import link.erpango.quip.utils.FileUtils
import link.erpango.quip.utils.PlatformUtils

/**
 * Esta clase es la encargada de crear las instancias de las clases que se comparten entre las plataformas.
 * Sus argumentos son instancias de las clases que se comparten entre las plataformas, como por ejemplo
 * la fuente de datos, el gestor de ficheros, el gestor de sonidos, el gestor de strings, etc.
 * */

class SharedFactory (
    val dataSource: IDataSource,
    platformManager: IPlatformManager,
    fileUtils: IFileUtils
) {
    val garage by lazy {
        GarageFactory(
            garageInteractor = GarageInteractor(dataSource),
            viewModelStoreOwner = ViewModelStoreOwnerImpl()
        )
    }

    val auth by lazy {
        AuthFactory(
            authInteractor = AuthInteractor(dataSource),
            viewModelStoreOwner = ViewModelStoreOwnerImpl()
        )
    }

    val tokenRefreshManager by lazy {
        TokenRefreshManager(dataSource)
    }

    val catalogSyncManager by lazy {
        CatalogSyncManager(dataSource)
    }

    val settings by lazy {
        SettingsFactory(
            authInteractor = AuthInteractor(dataSource),
            garageInteractor = GarageInteractor(dataSource),
            viewModelStoreOwner = ViewModelStoreOwnerImpl(),
            tokenRefreshManager = tokenRefreshManager
        )
    }

    val mySpareParts by lazy {
        MySparePartsFactory(
            mySparePartsInteractor = MySparePartsInteractor(dataSource),
            catalogRepository = dataSource.remote.catalog,
            userRepository = dataSource.remote.user,
            viewModelStoreOwner = ViewModelStoreOwnerImpl()
        )
    }

    val noteImport by lazy {
        NoteImportFactory(
            noteImportInteractor = NoteImportInteractor(
                extraction = dataSource.remote.extraction,
                catalog = dataSource.db.catalog,
                commonValues = dataSource.db.commonValues,
                garage = { dataSource.db.garage.selectAllVehicles() },
                logEvent = { name, param -> dataSource.firebase.analytics.logEvent(name, param) }
            ),
            bulkCreator = NoteImportBulkCreator(
                garage = GarageInteractor(dataSource),
                mySpareParts = MySparePartsInteractor(dataSource)
            ),
            loadGarage = { dataSource.db.garage.selectAllVehicles() },
            viewModelStoreOwner = ViewModelStoreOwnerImpl()
        )
    }

    init {
        PlatformUtils.create(platformManager = platformManager)
        FileUtils.create(fileUtils = fileUtils)
    }
}

class ViewModelStoreOwnerImpl: ViewModelStoreOwner {
    override val viewModelStore: ViewModelStore = ViewModelStore()
}